import React, { useEffect, useRef, useId } from "react";
import "./ConfirmDialog.css";

interface ConfirmDialogProps {
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = "Start new chat",
  cancelText = "Cancel",
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstButtonRef = useRef<HTMLButtonElement>(null);
  const secondButtonRef = useRef<HTMLButtonElement>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const messageId = useId();

  // Store the previously focused element and focus the first button on mount
  useEffect(() => {
    previousActiveElementRef.current = document.activeElement as HTMLElement;

    // Focus the first actionable button (cancel button)
    if (firstButtonRef.current) {
      firstButtonRef.current.focus();
    }

    // Cleanup: restore focus to previous element on unmount
    return () => {
      if (previousActiveElementRef.current) {
        previousActiveElementRef.current.focus();
      }
    };
  }, []);

  // Focus trap: prevent Tab/Shift+Tab from moving focus outside the dialog
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle Tab and Shift+Tab
      if (event.key !== "Tab") {
        return;
      }

      const activeElement = document.activeElement;
      const isFirstButtonFocused = activeElement === firstButtonRef.current;
      const isSecondButtonFocused = activeElement === secondButtonRef.current;

      // Only trap if focus is within the dialog buttons
      if (!isFirstButtonFocused && !isSecondButtonFocused) {
        return;
      }

      // Tab (forward): if on second button, move to first button
      if (!event.shiftKey && isSecondButtonFocused) {
        event.preventDefault();
        firstButtonRef.current?.focus();
        return;
      }

      // Shift+Tab (backward): if on first button, move to second button
      if (event.shiftKey && isFirstButtonFocused) {
        event.preventDefault();
        secondButtonRef.current?.focus();
        return;
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Listen for Escape key to close dialog
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCancel();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onCancel]);

  // Handle overlay click to close dialog
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onCancel();
    }
  };

  // Stop propagation on dialog container to prevent immediate close
  const handleDialogClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  return (
    <div className="confirm-dialog-overlay" onClick={handleOverlayClick}>
      <div
        ref={dialogRef}
        className="confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        onClick={handleDialogClick}
      >
        <h2 id={titleId}>{title}</h2>
        <p id={messageId}>{message}</p>
        <div className="confirm-dialog-buttons">
          <button ref={firstButtonRef} className="cancel-button" onClick={onCancel}>
            {cancelText}
          </button>
          <button ref={secondButtonRef} className="confirm-button" onClick={onConfirm}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
